"use client";

import { ArrowRight, SunMedium, Thermometer, MonitorPlay } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Capabilities() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const systems = [{
    number: "01",
    Icon: SunMedium,
    title: "Precision Lighting",
    description: "Sophisticated illumination systems that adapt to your environment, creating the perfect ambiance for every moment of your day."
  }, {
    number: "02",
    Icon: Thermometer,
    title: "Climate Control",
    description: "Intelligent thermal management that maintains your ideal comfort levels while optimizing energy efficiency throughout your space."
  }, {
    number: "03",
    Icon: MonitorPlay,
    title: "Integrated Media",
    description: "Seamlessly embedded audio and visual technology that delivers immersive experiences without compromising your interior design."
  }];
  return <section data-section-id="4669" id="capabilities" className="bg-background text-foreground relative overflow-hidden py-32 md:py-48 lg:py-[200px] px-6 md:px-12 lg:px-20">
      {}
      <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="lg:col-span-7 flex flex-col">
          <span className="font-default text-xs md:text-sm tracking-[0.25em] uppercase mb-10 md:mb-14 text-primary">
            — 03 / Our Core Capabilities
          </span>
          <h2 className="font-default font-bold leading-[0.95] tracking-tight text-foreground text-5xl md:text-7xl lg:text-[96px]">
            Advanced systems.
            <br />
            <span className="font-serif italic font-normal">Seamlessly unified.</span>
          </h2>
        </div>

        <div className="lg:col-span-5 lg:pt-6 flex items-end">
          <p className="font-default text-lg md:text-xl leading-relaxed text-foreground/80">
            We integrate lighting, climate, and media into a cohesive ecosystem, ensuring your environment responds intuitively to your needs.
          </p>
        </div>
      </div>

      {}
      <div ref={cardsRef} className="mt-24 md:mt-32 lg:mt-[140px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {systems.map((system, i) => {
        const Icon = system.Icon;
        return <div data-index={i} key={system.number} className={`group relative flex flex-col md:h-[520px] p-8 md:p-10 backdrop-blur-sm border transition-all duration-700 ease-out hover:-translate-y-2 bg-card border-border shadow-lg rounded-lg ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: cardsVisible ? `${i * 150}ms` : "0ms"
        }}>
              {}
              <div className="font-default text-xs tracking-[0.25em] uppercase text-primary">
                {system.number}
              </div>

              {}
              <div className="flex-1 flex items-center justify-center py-10">
                <Icon className="w-16 h-16 transition-all duration-500 text-primary" strokeWidth={1.5} />
              </div>

              {}
              <h3 className="font-default font-bold text-3xl md:text-4xl text-foreground leading-tight mb-5">
                {system.title}
              </h3>

              {}
              <p className="font-default text-base text-foreground/70 leading-relaxed mb-8">
                {system.description}
              </p>

              {}
              <Button variant="ghost" className="self-start px-0 h-auto font-default text-xs tracking-[0.2em] uppercase hover:bg-transparent group/btn text-primary">
                <span className="mr-2">→</span>
                <span>Learn more</span>
                <ArrowRight className="ml-1 w-3.5 h-3.5 opacity-0 -translate-x-2 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all duration-300" />
              </Button>
            </div>;
      })}
      </div>
    </section>;
}